import React, { useState, useEffect } from 'react';
import { Container, Grid, Box, TextField, Button, Typography, Divider, Select, MenuItem, FormControl, InputLabel, Autocomplete } from '@mui/material';
import axios from 'axios';

import Chat from '../../components/Chat/Chat';
import RentIndexSummary from '../../components/RentIndex/RentIndexSummary';

import { API_ROOT } from '../../config/config';

const ENDPOINT_SUFFIX = '/rent-index';
//const ENDPOINT_SUFFIX = '/rent-index-ai';

const axiosInstance = axios.create({
  headers: {
    authorization: 'a01d0f1d-fce7-49b7-9363-ecf33fd3e8a0'
  }
});


const RentalIndexPage = () => {
  const [newUserMessage, setNewUserMessage] = useState('');
  const [newAssistantMessage, setNewAssistantMessage] = useState('');
  const [simulatedUserMessage, setSimulatedUserMessage] = useState('');

  const [immoObject, setImmoObject] = useState({});
  const [location, setLocation] = useState(null);
  const [rentIndex, setRentIndex] = useState(null);
  const [status, setStatus] = useState(null);

  const [feedbackInput, setFeedbackInput] = useState('');
  const [showThankYou, setShowThankYou] = useState(false); // show thank you message
  const [resetChat, setResetChat] = useState(false); // flag to reset the chat

  const [exampleValue, setExampleValue] = useState('');



  const handleExampleChange = (event) => {
    const value = event.target.value;
    setExampleValue(value);
    setSimulatedUserMessage({ text: value });
  };

  const reactToNewUserMessage = (message) => {
    setNewUserMessage(message);
  };


  // Set initial chat message for users
  useEffect(() => {
    setNewAssistantMessage({ text: 'Willkommen beim Mietspiegel Assistenten!\n\nBitte geben Sie die Adresse des Objektes ein.' });
  }, []);


  // React to new user input via the chat component
  useEffect(() => {
    if (newUserMessage === '') return;

    console.log(`New user message:`);
    console.log(newUserMessage);

    // Call the api with the user message
    const messageData = {
      message: newUserMessage.text,
      immoObject: immoObject
    }
    axiosInstance.post(API_ROOT + ENDPOINT_SUFFIX, messageData)
    .then((res) => {
      console.log(res);
      if (res.status !== 200) {
        console.log("Error when calling rent-index api:", res);
        setNewAssistantMessage({ text: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.' })
        return;
      }

      // Ensure that all required attributes are present in the return object
      const data = res.data;
      if ('status' in data === false || 'location' in data === false || 'immoObject' in data === false || 'rentIndex' in data === false) {
        console.log("Error: Invalid response from rent-index api:", data);
        setNewAssistantMessage({ text: 'Ein Fehler ist aufgetreten, da das System die Berechnung nicht vollständig durchführen konnte. Bitte versuchen Sie es erneut.' })
        return;
      }

      setImmoObject(data.immoObject);
      setLocation(data.location);
      setRentIndex(data.rentIndex);
      setStatus(data.status);

      const assistantMessage = createNextAssistantMessage(data);
      setNewAssistantMessage({ text: assistantMessage });
    })
    .catch((err) => {
      console.log("Error when calling rent-index api:", err);
      setNewAssistantMessage({ text: 'Ein Fehler ist beim der Verarbeitung der Mietspiegel API Ergebnisse aufgetreten. Bitte versuchen Sie es erneut.' })
    });

  }, [newUserMessage]);


  const createNextAssistantMessage = (data) => {
    const tmpImmoObject = data.immoObject;
    const tmpRentIndex = data.rentIndex;

    let assistantMessage = '';

    if ('address' in tmpImmoObject === false) {
      assistantMessage += `Bitte geben Sie die Adresse des Objektes ein. Wir prüfen dann, ob ein Mietspiegel verfügbar ist.`;
      return assistantMessage;
    }

    const rentIndexGeneral = tmpRentIndex?.general;
    if (rentIndexGeneral) {
      if (rentIndexGeneral.methods.length) {
        let rentIndexType = 'unbekannter';
        if (rentIndexGeneral.type === 'QUALIFIED') {
          rentIndexType = 'qualifizierter';
        } else if (rentIndexGeneral.type === 'SIMPLE') {
          rentIndexType = 'einfacher';
        }
        assistantMessage += `Für die Adresse ${tmpImmoObject.address} ist ein ${rentIndexType} Mietspiegel verfügbar.`;
        assistantMessage += ` Er ist gültig von ${rentIndexGeneral.validFrom.substring(0,10)} bis ${rentIndexGeneral.validUntil.substring(0,10)}.`;
        assistantMessage += ` Die Kappungsgrenze liegt bei ${Math.round(rentIndexGeneral.capLimit*100)}%.`;
      }
      else if (rentIndexGeneral.methods.length === 0) {
        assistantMessage += `Für diese Adresse ist kein Mietspiegel verfügbar.`;
        return assistantMessage;
      }
      else { // null
        assistantMessage += `Bitte überprüfen Sie, ob ein Mietspiegel verfügbar ist. In der Datenbank konnte noch kein entsprechender Eintrag gefunden werden.`;
        return assistantMessage;
      }
    }
    else {
      assistantMessage += `Es ist leider ein Fehler aufgetreten. Bitte versuchen Sie es mit einer neuen Eingabe erneut.`;
      return assistantMessage;
    }

    // Show the used methods
    const rentIndexMethod = tmpRentIndex?.status?.method;
    if (rentIndexMethod) {
      assistantMessage += `\n\nDie Berechnung wurde mit der folgenden Methode durchgeführt: ${rentIndexMethod}`;
    }

    const rentIndexResult = tmpRentIndex?.result;

    let mandatoryAttributesMissing = false;
    const mandatoryAttributes = tmpRentIndex?.attributes?.missing?.mandatory;
    if (mandatoryAttributes && mandatoryAttributes.length > 0) {
      assistantMessage += `\n\nEs fehlen noch die folgenden Pflichtangaben:\n${mandatoryAttributes.map(attr => `- ${attr.longForm}: ${attr.description}`).join('\n')}`;
      mandatoryAttributesMissing = true;
    }
    //const optionalAttributes = tmpRentIndex?.attributes?.missing?.optional;
    //if (optionalAttributes && optionalAttributes.length > 0) {
      //assistantMessage += `\n\nSie können die Berechnung mit folgenden optionalen Angaben verbessern:\n${optionalAttributes.map(attr => `- ${attr.longForm}: ${attr.description}`).join('\n')}`;
    //}
    if (mandatoryAttributesMissing) {
      return assistantMessage;
    }


    // Show the steps first. It might be relevant when checking invalidations or errors.
    const rentIndexSteps = tmpRentIndex?.result?.steps;
    if (rentIndexSteps && rentIndexSteps.length > 0) {
      assistantMessage += `\n\nDie durchschnittliche Miete beträgt ${tmpRentIndex.result.mean.toFixed(2)} €/m² mit einer Spanne von ${tmpRentIndex.result.low.toFixed(2)} - ${tmpRentIndex.result.high.toFixed(2)} €/m².`;
      if (rentIndexResult.meanTotal && rentIndexResult.lowTotal && rentIndexResult.highTotal) {
        assistantMessage += `\nFür die angegebene Wohnungsgröße beträgt die gesamte Miete damit ${rentIndexResult.meanTotal.toFixed(2)} € mit einer Spanne von ${rentIndexResult.lowTotal.toFixed(2)} - ${rentIndexResult.highTotal.toFixed(2)} €.`;
      }
      assistantMessage += `\n\nDie folgende Schritte wurden für die Berechnung durchgeführt:`;
      //assistantMessage += `\n- Die Basismiete beträgt ${tmpRentIndex.result.base.toFixed(2)} €/m².`;
      assistantMessage += `\n${rentIndexSteps.map(step => `- ${step}`).join('\n')}`;
    }

    // Show the invalidation reasons and errors.
    const rentIndexStatus = tmpRentIndex?.status;
    if (rentIndexStatus.valid === false) {
      assistantMessage += `\n\nEs konnte mit den eingegebenen Daten leider keine Miete berechnet werden.`;
      assistantMessage += `\n\nBegründung:\n${rentIndexStatus.invalidationReasons.map(reason => `- ${reason}`).join('\n')}`;
    }
    if (rentIndexStatus.error === true) {
      assistantMessage += `\n\nEs ist ein Fehler aufgetreten.`;
      assistantMessage += `\n\nBegründung:\n${rentIndexStatus.errors.map(reason => `- ${reason}`).join('\n')}`;
    }
    if (rentIndexStatus.valid === false || rentIndexStatus.error === true) {
      return assistantMessage;
    }




    return assistantMessage;
  }

  const createResetMessage = () => {
    setResetChat(true);

    setImmoObject({});
    setLocation(null);
    setRentIndex(null);
    setStatus(null);

    setTimeout(() => setResetChat(false), 200);
    setTimeout(() => setNewAssistantMessage({ text: "Willkommen beim Mietspiegel Assistenten!\n\nBitte geben Sie die Adresse des Objektes ein." }), 200);
  }


  const handleFeedbackInputChange = (event) => {
    setFeedbackInput(event.target.value);
  };

  
  const buildFeedbackMessage = (type, message) => {
    const data = {
      rentIndexFeedback : {
        type: type,
        message: message,
        newUserMessage: newUserMessage,
        newAssistantMessage: newAssistantMessage,
        immoObject: immoObject,
        location: location,
        rentIndex: rentIndex,
        status: status,
      }
    }

    return data;
  };

  const sendErrorExample = () => {
    const data = buildFeedbackMessage('error', feedbackInput);
    axiosInstance.post(API_ROOT + ENDPOINT_SUFFIX + '/feedback', data)
    .then((res) => {
      if (res.status !== 200) {
        console.log("Error when calling rent-index api:", res);
        return;
      } else {
        console.log("Feedback sent successfully");
      }
    })
    .catch((err) => {
      console.log("Error when calling rent-index feedback api:", err);
    });

    // Clear the text box
    setFeedbackInput('');

    // Show thank you message
    setShowThankYou(true);
    setTimeout(() => setShowThankYou(false), 3000);
  }

  const sendGoodExample = () => {
    const data = buildFeedbackMessage('success', feedbackInput);
    axiosInstance.post(API_ROOT + ENDPOINT_SUFFIX + '/feedback', data)
    .then((res) => {
      if (res.status !== 200) {
        console.log("Error when calling rent-index api:", res);
        return;
      } else {
        console.log("Feedback sent successfully");
      }
    })
    .catch((err) => {
      console.log("Error when calling rent-index feedback api:", err);
    });

    // Clear the text box
    setFeedbackInput('');

    // Show thank you message
    setShowThankYou(true);
    setTimeout(() => setShowThankYou(false), 3000);
  }

  const sortByLabel = (list) => {
    return list.sort((a, b) => {
      // Handle empty label case
      if (a.label === '') return -1;
      if (b.label === '') return 1;
      
      // Compare labels alphabetically
      return a.label.localeCompare(b.label, 'de', { sensitivity: 'base' });
    });
  }
  const sortedList = sortByLabel([
    { label: '', value: "" },
    { label: 'Kiel', value: "Am Dorfplatz 14 in Kiel. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Lübeck', value: "Mühlenbrücke 14 in Lübeck. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Hamburg', value: "Friedrich-Ebert-Straße 22 in Hamburg. Wohnung mit 83.4 m² und Baujahr ist 1999." },
    { label: 'Salzgitter', value: "Hauptstraße 1 in Salzgitter. Wohnung mit 80 m² und Baujahr ist 1975." },
    { label: 'Wolfsburg', value: "Roseggerstr 3 in Wolfsburg. Wohnung mit 80 m² und Baujahr ist 1975." },
    { label: 'Wolfenbüttel', value: "Leinder Straße 10 in Wolfenbüttel. Wohnung mit 80 m² und Baujahr ist 1975." },
    { label: 'Hannover', value: "Teststraße 1 in Hannover. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Hildesheim', value: "Rosenhagen 1 in Hildesheim. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Hansestadt Lüneburg', value: "Ochtmisser Kirchsteig 60 in Hansestadt Lüneburg. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Oldenburg', value: "Teststraße 1 in Oldenburg. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Osnabrück', value: "Schützenstraße 5, 76327 Osnabrück. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Bremen', value: "Parkallee 117 in Bremen. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Bremerhaven', value: "Hinrich-Schmalfeldt-Straße 4 in Bremerhaven. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Düsseldorf', value: "Teststraße 1 in Düsseldorf. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Krefeld', value: "Teststraße 1 in Krefeld. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Oberhausen', value: "Teststraße 1 in Oberhausen. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Remscheid', value: "Paul-Krause-Str. 7 in Remscheid. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Solingen', value: "Teststraße 1 in Solingen. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Neuss', value: "Teststraße 1 in Neuss. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Bergisch Gladbach', value: "Teststraße 1 in Bergisch Gladbach. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Bottrop', value: "Hauptstraße 1 in Bottrop. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Recklinghausen', value: "Mulvanyring 1 in Recklinghausen. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Bielefeld', value: "Teststraße 1 in Bielefeld. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Gütersloh', value: "Teststraße 1 in Gütersloh. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Rheda-Wiedenbrück', value: "Teststraße 1 in Rheda-Wiedenbrück. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Hamm', value: "Teststraße 1 in Hamm. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Herne', value: "Hauptstraße 1 in Herne. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Siegen', value: "Hofbachstraße 45, 57078 Siegen. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Offenbach am Main', value: "Biberstraße 7, 83098 Offenbach am Main. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Koblenz', value: "Hauptstraße 1 in Koblenz. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Trier', value: "Von-Reiche-Straße 33 in Trier. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Heilbronn', value: "Leonhardstraße 4/1 in Heilbronn. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Heidelberg', value: "Teststraße 1 in Heidelberg. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Pforzheim', value: "Hauptstraße 1 in Pforzheim. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Reutlingen', value: "Teststraße 1 in Reutlingen. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Ulm', value: "Donaustraße 5/1 in Ulm. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Ingolstadt', value: "Zanderweg 5 in Ingolstadt. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Regensburg', value: "Adolf-Münzer-Straße 14 in Regensburg. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Erlangen', value: "Walter-Flex-Straße 10 in Erlangen. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Fürth', value: " Poppenreuther Str. 3 in Fürth. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Nürnberg', value: "Teststraße 1 in Nürnberg. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Würzburg', value: "Versbacher Röthe 66 in Würzburg. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Neu-Ulm', value: "Tiefenbach 2 in Neu-Ulm. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Saarbrücken', value: "Aschbachring 11 in Saarbrücken. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Berlin', value: "Aachener Str. 1 in Berlin, 45 qm und Baujahr 1976" },
    { label: 'Potsdam', value: "Teststraße 1 in Potsdam. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Rostock', value: "Neuer Weg 1 in Rostock. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Magdeburg', value: "Am Vogelgesang 12 in Magdeburg. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Erfurt', value: "Teststraße 1 in Erfurt. Wohnung mit 80 m² und Baujahr 1975." },
    { label: 'Jena', value: "Teststraße 1 in Jena. Wohnung mit 80 m² und Baujahr 1975." },

    { label: 'Ludwigsburg', value: "Haldenstraße 29 in Ludwigsburg. 95 qm, Baujahr 1950. Die Wohnung liegt im fünften Stock und es gibt einen Aufzug." },
    { label: 'Mannheim', value: "Abendröte 25 in Mannheim. 68,3 qm, Baujahr 1980." },
    { label: 'Ludwigshafen', value: "Schumannstraße 5 in Ludwigshafen. 40 qm und Baujahr 1990." },
    { label: 'Aachen', value: "Freunder Landstraße 119 in Aachen. 70 qm und Baujahr 1923." },
    { label: 'Offenburg', value: "Am Gerberbach 2 in Offenburg. 53 qm und Baujahr 1913." },
    

    
    { label: '[AI_CODE] Peine', value: "Beethovenstraße 11, 31224 Peine. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Speyer', value: "Albert-Einstein-Straße 15 in Speyer. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Rastatt', value: "Kolberger Straße 3 in Rastatt. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Elmshorn', value: "Schulstraße 15-17, 25335 Elmshorn. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Plauen', value: "Albertplatz 9 in Plauen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Delmenhorst', value: "Oldenburger Straße 9, 27753 Delmenhorst. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Singen', value: "Hohgarten 2, 78224 Singen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Freising', value: "Obere Hauptstraße 2, 85354 Freising. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Frankenthal', value: "Rathausplatz 2-7, 67227 Frankenthal (Pfalz). 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Baden-Baden', value: "Marktplatz 3, 76530 Baden-Baden. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Sankt Augustin', value: "Markt 1 53757 Sankt Augustin. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Nordhorn', value: "Jahnstraße 2, 48529 Nordhorn. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Bad Homburg v. d. Höhe', value: "Nehringstraße 7-9, 61352 Bad Homburg v. d. Höhe. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Bad Salzuflen', value: "Rudolph-Brandes-Allee 19, 32105 Bad Salzuflen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Passau', value: "Innsstraße 27, 94032 Passau. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Ahlen', value: "Kantstraße 15 in Ahlen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Ravensburg', value: "Reichlestraße 21 in Ravensburg. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Stralsund', value: "Zum Seglerhafen 3 in Stralsund. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Rosenheim', value: "An der Burgermühle 10 in Rosenheim. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Friedrichshafen', value: "Hochstraße 1 in Friedrichshafen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Bergkamen', value: "Rathausplatz 1 59192 Bergkamen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Unna', value: "Rathausplatz 1, 59423 Unna. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Bönen', value: "Am Bahnhof 7; 59199 Bönen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Fröndenberg', value: "Bahnhofstraße 2 58730 Fröndenberg. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Holzwickede', value: "Allee 5 59439 Holzwickede. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Kamen', value: "Rathausplatz 1 59174 Kamen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Selm', value: "Adenauerplatz 2 59379 Selm. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Werne', value: "Konrad-Adenauer-Platz 1 59368 Werne. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Langenfeld', value: "Trompeter Straße 10 in Langenfeld. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Greifswald', value: "Markt 1 in 17489 Greifswald. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Schwäbisch Gmünd', value: "Marktplatz 37 in Schwäbisch Gmünd. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Lorch', value: "Hauptstraße 19 in Lorch. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Göggingen', value: "Hauptstraße 46 in Göggingen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Eschach', value: "Rathausplatz 1 in Eschach. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Iggingen', value: "Marktplatz 6 73574 Iggingen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Leinzell', value: "Mulfinger Straße 2 73575 Leinzell. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Obergröningen', value: "Seestraße 12 73569 Obergröningen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Schechingen', value: "Marktplatz 1 73579 Schechingen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Bartholomä', value: "Brunnenfeldstraße 1 in Bartholomä. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Böbingen', value: "Römerstraße 2 in 73560 Böbingen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Heubach', value: "Hauptstraße 53 73540 Heubach. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Heuchlingen', value: "Küferstraße 3 73572 Heuchlingen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Mögglingen', value: "Zehnthof 1 in 73563 Mögglingen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Waldstetten', value: "Zur Vorstatt 19; 73550 Waldstetten. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Mutlangen', value: "Hauptstraße 22 73557 Mutlangen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Sindelfingen', value: "Ziegelstraße 10/1 in Sindelfingen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Böblingen', value: "Reinhold-Nägele-Weg 5 in Böblingen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Neuwied', value: "Engerser Landstraße 17 56564 Neuwied. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Herzebrock-Clarholz', value: "Am Rathaus 1 in 33442 Herzebrock-Clarholz. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Langenberg', value: "Klutenbrinkstraße 5 in Langenberg. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Borgholzhausen', value: "Schulstraße 5 in Borgholzhausen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Werther', value: "Schwarzbachtal 51 in Werther. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Steinhagen', value: "Am Pulverbach 25 33803 Steinhagen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Versmold', value: "Gestermannstr. 1 in Versmold. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Harsewinkel', value: "Münsterstraße 4 in Harsewinkel. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Schloß Holte-Stukenbrock', value: "Holter Str. 4-6 in 33758 Schloß Holte-Stukenbrock. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Rietberg', value: "Rügenstraße 1 33397 Rietberg. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Verl', value: "Paderborner Straße 5 33415 Verl. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Halle (Westfalen)', value: "Rosenstraße 9 in Halle (Westfalen). 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Rüsselsheim am Main', value: "Marktplatz 4 65428 Rüsselsheim am Main. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Aalen', value: "Dekanstraße 9 in Aalen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Essingen', value: "Lindensteige 8, 73457 Essingen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Fulda', value: "Schlossstraße 1 in Fulda. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Kempten im Allgäu', value: "Rathausplatz 29 in Kempten im Allgäu. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Bocholt', value: "Südwall 19 in Bocholt. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Aschaffenburg', value: "Zum Kreus 6 in Aschaffenburg. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Brandenburg an der Havel', value: "Weinmeisterweg 2 in Brandenburg an der Havel. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Castrop-Rauxel', value: "Brucknerstraße 33 in Castrop-Rauxel. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Arnsberg', value: "Richters Köppken 13 in Arnsberg. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Bayreuth', value: "Luitpoldplatz 13 in Bayreuth. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Gladbeck', value: "Willy-Brandt-Platz 2 in Gladbeck. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Troisdorf', value: "Kölner Straße 176 in Troisdorf. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Dessau-Roßlau', value: "Zerbster Straße 4 in Dessau-Roßlau. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Bamberg', value: "Maximiliansplatz 3 in Bamberg. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Minden', value: "Kleiner Domhof 17 in Minden. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Marl', value: "Carl-Duisberg-Straße 165 in Marl. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Zwickau', value: "Hauptmarkt 1 in Zwickau. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Flensburg', value: "Rathausplatz 1 in Flensburg. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Reichenau', value: "Moosweg 16 in Reichenau. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Allensbach', value: "Hörenbergstraße 28a in Allensbach. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Konstanz', value: "Gartenstraße 56 in Konstanz. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Villingen-Schwenningen', value: "Kaiserring 10 in Villingen-Schwenningen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Eslohe (Sauerland)', value: "Schultheißstraße 2 59889 Eslohe. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Dresden', value: "Würzburger Straße 45 in Dresden. 59 m² und Baujahr 2011." },
    { label: '[AI_CODE] Meschede', value: "Franz-Stahlmecke-Platz 2 59872 Meschede. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Augsburg', value: "Südtiroler Straße 4 in Augsburg. 77 m² und Baujahr 2012." },
    { label: '[AI_CODE] Iserlohn', value: "Schillerplatz 7 58636 Iserlohn. 72 qm und Baujahr 1943." },
    { label: '[AI_CODE] Leverkusen', value: "Clemens-Winkler-Straße 10, 51373 Leverkusen. 73 m² und Baujahr 1950." },
    { label: '[AI_CODE] Wuppertal', value: "Schusterstraße 44 in Wuppertal. Die Wohnung hat 35 m² und das Baujahr ist 1949. Sie hat einen gefangnene Raum, eine große Terrasse mit ca. 10 m² Fläche. Außerdem wurden alle Außenwände in 2010 gedämmt." },
    { label: '[AI_CODE] Gera', value: "Weg der Freundschaft 4 07546 Gera. Wohnung mit 43 m² und Baujahr 1945. Normale Ausstattung." },
    { label: '[AI_CODE] Hallenberg', value: "Rathausplatz 1. 59969 Hallenberg. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Halle (Saale)', value: "Große Brauhausstraße 5, 06108 Halle (Saale). 37 m² und Baujahr 1957." },
    { label: '[AI_CODE] Leipzig', value: "Robert-Volkmann-Straße 4c in Leipzig. Die Wohnung hat 138,2 m² und Baujahr 1990. Es ist eine Penthauswohnung im Plattenbau. Es gibt Fußbodenheizung und eine große Terrasse mit über 10m². Eine offene Einbauküche ist ebenfalls vorhanden. Leider gibt es einen unverkleideten Spülkasten im Bad. Der Boden ist Standardlaminat und es gibt Dreifachverglasung." },
    { label: '[AI_CODE] Chemnitz', value: "Hauptstraße 1 in Chemnitz. 100 qm und Baujahr 1950. Es gibt eine Lüftung im Bad, aber kein Fenster. Das Bad ist gefliest. In der Küche gibt es eine Einbauküche. Bodenbelag ist durchgängig Parkett. Außerdem ist die Wohnung barrierefrei. In der Umgebung gibt es starken Lärm trotz der offenen Bebauung." },
    { label: '[AI_CODE] Neubrandenburg', value: "Friedländer Weg 4 in 17033 Neubrandenburg. Baujahr 1965 und 53,5 m². Es gibt einen Verbrauchsausweis mit 105 kWh/m²a. Die Wohnung ist barrierefrei und es gibt einen abschließbaren Fahrradkeller. Dusche und Badewanne gibt es beides. Und es gibt hochwertigen Designfußboden." },
    { label: '[AI_CODE] Schwerin', value: "Mecklenburgstraße 10 in Schwerin, 80 m² und Baujahr 1975. Im Bad gibt es eine einfache Sanitärausstattung. Es wurden die folgenden vier Modernisierungsmaßnahmen durchgeführt (alle ab 2023): Fußboden, Innentüren, Außenwanddämmung und Spielflächen" },
    { label: '[AI_CODE] München', value: "Haydnstrasse 6, 80336 München, Wohnung, Baujahr 2014, Wohnfläche 66.39, Anzahl Zimmer 2" },
    { label: '[AI_CODE] Tübingen', value: "Knollstraße 13 in Tübingen. 43,2 m² und Baujahr 1953. Es gibt Teppichboden in der Wohnung und sie liegt im Souterrain. Ein Aufzug ist vorhanden sowie ein zweites Gästebad." },
    { label: '[AI_CODE] Freiburg im Breisgau', value: "Draisstraße 23 in Freiburg im Breisgau. 40 m² und Baujahr 1970. Es gibt eine große Terrasse mit 15 m² Fläche." },
    { label: '[AI_CODE] Karlsruhe', value: "Mombertstraße 10, 76131 Karlsruhe. 100 m² und Baujahr 1970." },
    { label: '[AI_CODE] Esslingen am Neckar', value: "Rathausplatz 2. 73728 Esslingen am Neckar , 80 m² und Baujahr 1975. Im Bad gibt es eine einfache Sanitärausstattung. Es wurden die folgenden vier Modernisierungsmaßnahmen durchgeführt (alle ab 2023): Fußboden, Innentüren, Außenwanddämmung und Spielflächen" },
    { label: '[AI_CODE] Stuttgart', value: "Heusteigstraße 106 in Stuttgart. 100 m² und Baujahr 1960. Parkett in der ganzen Wohnung und hochwertige Sanitärausstattung." },
    { label: '[AI_CODE] Mainz', value: "Uwe-Bayer-Straße 4 in Mainz. 59 m² und Baujahr 2013." },
    { label: '[AI_CODE] Kaiserslautern', value: "Mannheimer Str. 139, 67657 Kaiserslautern. 80 qm und Baujahr 1975." },
    { label: '[AI_CODE] Kassel', value: "Langenbeckstraße 32 in Kassel. 43 m² und Baujahr 1974." },
    { label: '[AI_CODE] Gießen', value: "Hermann-Löns-Str. 47 in Gießen. 85 m² und Baujahr 1970." },
    { label: '[AI_CODE] Hanau', value: "Grüner Weg 3 in Hanau mit 40 m² und Baujahr 1950. Die Wohnung hat einen schlechten Grundriss. Dafür hat sie eine hochwertige Einbauküche und ein super Bad mit Badewanne und Dusche sowie ein Zweitbad. Der Fußboden ist hochwertiger Parkett." },
    { label: '[AI_CODE] Wiesbaden', value: "Hauptstraße 1 in Wiesbaden. 100 qm und Baujahr 2022. Die Wohnung hat einen schlechten Grundriss. Dafür hat sie eine hochwertige Einbauküche und ein super Bad mit Badewanne und Dusche sowie ein Zweitbad. Der Fußboden ist hochwertiger Parkett." },
    { label: '[AI_CODE] Frankfurt', value: "Friedrichstraße 10 in Frankfurt. Baujahr 1994 und 147 m². Es gibt einen einfachen Heizungsstandard und die Leitungen sind auf Putz verlegt. Eine Videosprechanlage ist vorhanden und es gibt zwei Badezimmer. Die Wohnung hat Dreifachverglasung und überall elektrische Rollläden." },
    { label: '[AI_CODE] Darmstadt', value: "Thylmannweg 4 in Darmstadt. 47 m² und Baujahr 1995." },
    { label: '[AI_CODE] Schwerte', value: "Rathausstraße 31 58239 Schwerte. 72 qm und Baujahr 1943." },
    { label: '[AI_CODE] Werdohl', value: "Goethestraße 51 58791 Werdohl. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Schalksmühle', value: "Rathausplatz 1, 58579 Schalksmühle. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Plettenberg', value: "Grünestraße 12 58840 Plettenberg. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Neuenrade', value: "Alte Burg 1 58809 Neuenrade. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Nachrodt-Wiblingwerde', value: "Hagener Straße 76 58769 Nachrodt-Wiblingwerde. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Menden', value: "Neumarkt 5, 58706 Menden. 72 qm und Baujahr 1943." },
    { label: '[AI_CODE] Meinerzhagen', value: "Bahnhofstr. 9, 58540 Meinerzhagen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Lüdenscheid', value: "Rathausplatz 2 58507 Lüdenscheid. 72 qm und Baujahr 1943." },
    { label: '[AI_CODE] Kierspe', value: "Springerweg 21 58566 Kierspe. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Herscheid', value: "Plettenberger Straße 27, 58849 Herscheid. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Hemer', value: "Rathaus Hademareplatz 44 58675 Hemer. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Halver', value: "Thomasstraße 18 58553 Halver. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Balve', value: "Widukindplatz 1 58802 Balve. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Altena', value: "Lüdenscheider Straße 22 58762 Altena. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Winterberg', value: "Am Waltenberg 69 59955 Winterberg. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Sundern (Sauerland)', value: "Rathausplatz 1 59846 Sundern. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Schmallenberg', value: "Unterm Werth 1. 57392 Schmallenberg. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Olsberg', value: "Hüttenstraße 38. 59939 Olsberg. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Medebach', value: "Österstr. 1 59964 Medebach. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Marsberg', value: "Rathaus Lillers-Straße 8, 34431 Marsberg. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Brilon', value: "Bahnhofstraße 32. 59929 Brilon. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Bestwig', value: "Rathausplatz 1. 59909 Bestwig. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Witten', value: "Auf dem Schnee 59 in Witten. 85 m² und Baujahr 1970." },
    { label: '[AI_CODE] Hagen', value: "Hochstraße 128 in Hagen. 73 m² und Baujahr 1974. Die Ausstattung der Wohnung ist gut. Es gibt außerdem ein Gäste-WC und eine große Terrasse." },
    { label: '[AI_CODE] Dortmund', value: "Kaiserstraße 180 in Dortmund. Die Wohnung hat 57 m² und das Baujahr ist 1953." },
    { label: '[AI_CODE] Paderborn', value: "Krumme Grube 6 in Paderborn. 38,3 m² und Baujahr 1934. Die Wohnung im Souterrain hat eine offene Küche und ist barrierefrei." },
    { label: '[AI_CODE] Münster', value: "Klemensstraße 10 48143 Münster, 80 m² und Baujahr 1980." },
    { label: '[AI_CODE] Düren', value: "Wohnung. Kaiserplatz 2 in Düren, Baujahr 1978, 75 m² Wohnfläche, ruhiges Wohnviertel am Stadtrand in der Nähe eines kleinen Parks, renoviertes Badezimmer mit Dusche, zentrale Gasheizung vor 5 Jahren erneuert, isolierverglaste Fenster, kleine Einbauküche, Balkon mit Blick ins Grüne, gut gepflegt, vor 3 Jahren teilweise renoviert mit neuem Bodenbelag und frisch gestrichen." },
    { label: '[AI_CODE] Gelsenkirchen', value: "Ebertstraße 11, 45879 Gelsenkirchen. 52 m² und Baujahr 1949. Es gibt eine barrierefreie Dusche und hochwertige Bodenbeläge. Das Bad hat kein Tageslicht, sondern nur eine Belüftung." },
    { label: '[AI_CODE] Bonn', value: "Mauspfad 4 in Bonn. 63,5 m² und Baujahr 1957." },
    { label: '[AI_CODE] Mörs', value: "Bonifatiusstraße 72 47441 Moers mit 40 m² und Baujahr 1934." },
    { label: '[AI_CODE] Ratingen', value: "Minoritenstr. 2, 40878 Ratingen. 75 qm und Baujahr 1970." },
    { label: '[AI_CODE] Göttingen', value: "Humboldtallee 26 in Göttingen. 100 m² und Baujahr 1970. Es gibt eine große Terrasse mit 10 m² Fläche. Außerdem wurden alle Außenwände in 2010 gedämmt." },
    { label: '[AI_CODE] Mühlheim an der Ruhr', value: "Auf dem Dudel 9, Mühlheim an der Ruhr. 41 m² und Baujahr 1973." },
    { label: '[AI_CODE] Mönchengladbach', value: "Weiherstraße 21, 41061 Mönchengladbach. 100 qm und Baujahr 2022. " },
    { label: '[AI_CODE] Duisburg', value: "Fliederstraße 41 in Duisburg. Die Wohnung hat 35 m² und das Baujahr ist 2022." },
    { label: '[AI_CODE] Essen', value: "Abteistraße 21 in Essen. 32 m² und Baujahr 1976." },
    { label: '[AI_CODE] Braunschweig', value: "Wilhelmstraße 87 in Braunschweig. 50 m² und Baujahr 1975." },

  ]);


  return (
    <Container>

      {/* Spacer */}
      <Box mt={4} />

      {/*
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={12}>
          <Typography variant="h4">Mietspiegel Assistent</Typography>
        </Grid>
      </Grid>
      */}


      {/* Spacer */}
      <Box mt={4} />


      <Grid container spacing={2} alignItems="center">

        <Grid item xs={12} sm={12}>
          <Chat newAssistantMessage={newAssistantMessage} onNewUserMessage={reactToNewUserMessage} simulatedUserMessage={simulatedUserMessage} resetChat={resetChat} />
        </Grid>

        {/*
        <Grid item xs={12} sm={4} container direction="column" alignItems="center" justifyContent="center">
          <RentIndexSummary immoObject={immoObject} />
        </Grid>
        */}

      </Grid>


      <Box mt={4} />
      <Divider 
        sx={{ 
          borderBottomWidth: 2,
          borderColor: 'primary.main',
          my: 4  // Adds margin top and bottom
        }} 
      /> 
      <Box mt={4} />


      <Grid container item xs={12} direction="row" spacing={2}>
        <Grid item xs={12}>
          <Autocomplete
            id="example-select"
            options={sortedList}
            value={exampleValue}
            onChange={(event, newValue) => {
              setExampleValue(newValue ? newValue.value : '');
              setSimulatedUserMessage({ text: newValue ? newValue.value : '' });
            }}
            renderInput={(params) => <TextField {...params} label="Beispieladresse auswählen" />}
            isOptionEqualToValue={(option, value) => option.value === value}
            fullWidth
          />
        </Grid>
      </Grid>

      <Box mt={2} />

      <Grid container item xs={12} direction="row" spacing={2}>
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            onClick={createResetMessage}
          >
            Reset
          </Button>
        </Grid>
      </Grid>

      {/* Spacer */}
      <Box mt={4} />
      <Divider 
        sx={{ 
          borderBottomWidth: 2,
          borderColor: 'primary.main',
          my: 4  // Adds margin top and bottom
        }} 
      />
      <Box mt={4} />


      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={8}>
          <Typography variant="h6">Feedback</Typography>
        </Grid>
      </Grid>

      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={8}>
          <Box
            sx={{
              border: '1px solid rgba(0, 0, 0, 0.23)',
              borderRadius: '4px 0 0 4px',
              padding: '14px',
              height: '100%',
            }}
            >
            <textarea
              style={{
                width: '100%',
                height: 'auto',
                border: 'none',
                outline: 'none',
                resize: 'none',
                fontFamily: 'inherit',
                fontSize: 'inherit',
                overflowY: 'auto',
              }}
              rows={1}
              value={feedbackInput}
              onChange={handleFeedbackInputChange}
            />
          </Box>
          {/* I had to remove this due to a bug in the TextField component. In case it gets fixed, reactive it.
          <TextField
          sx={{
            '& .MuiOutlinedInput-root': {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            },
          }}
          variant="outlined"
          fullWidth
          multiline
          rows={1}
          value={feedbackInput}
          onChange={handleFeedbackInputChange}
          />
          */}
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button
            fullWidth
            variant="contained"
            color="error"
            onClick={sendErrorExample}
          >Fehler melden
          </Button>
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button
            fullWidth
            variant="contained"
            color="success"
            onClick={sendGoodExample}
          >Bestätigen
          </Button>
        </Grid>
      </Grid>

      {showThankYou && (
        <Grid item xs={12} sm={12}>
          <Box mt={3} />
          <Typography variant="h4" sx={{ transition: 'opacity 1s', opacity: showThankYou ? 1 : 0, color: 'green', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>Vielen Dank für das Feedback!</Typography>
        </Grid>
      )}


      {/* Spacer */}
      <Box mt={8} />

    </Container>
  );
};

export default RentalIndexPage;
